import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    TextInput,
    RefreshControl,
    ActivityIndicator,
    StatusBar,
    Image,
    Platform,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import * as ImagePicker from 'expo-image-picker';
import axios from 'axios';
import { COLORS, API, APP_INFO } from '../config';
import { useAuth } from '../context/AuthContext';
import { useCustomAlert } from '../components/CustomAlert';

export default function TopupScreen({ navigation }) {
    const { user, refreshUser } = useAuth();
    const { alert } = useCustomAlert();
    const [amount, setAmount] = useState('');
    const [proofImage, setProofImage] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Bank settings from API
    const [bankSettings, setBankSettings] = useState({
        bank_name: 'BCA',
        bank_account: '1234567890',
        bank_holder: 'Ming Digital',
        min_topup: '10000',
    });

    const quickAmounts = [20000, 50000, 100000, 200000, 500000];

    const fetchHistory = async () => {
        try {
            const [historyRes, settingsRes] = await Promise.all([
                axios.get(API.TOPUP.HISTORY(user.id)),
                axios.get(API.SETTINGS.TOPUP).catch(() => ({ data: { success: false } })),
            ]);

            if (historyRes.data.success) {
                setHistory(historyRes.data.data || []);
            }

            if (settingsRes.data.success && settingsRes.data.data) {
                setBankSettings(prev => ({ ...prev, ...settingsRes.data.data }));
            }
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            refreshUser();
            fetchHistory();
        }, [])
    );

    const onRefresh = () => {
        setRefreshing(true);
        refreshUser();
        fetchHistory();
    };

    const formatNumber = (num) => {
        return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') || '0';
    };

    const formatCurrency = (num) => {
        return 'MG ' + formatNumber(Math.floor(num || 0));
    };

    const pickImage = async () => {
        try {
            const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
            if (status !== 'granted') {
                alert('Permission Denied', 'Izinkan akses gallery untuk upload bukti transfer');
                return;
            }

            // Determine MediaType safely
            let mediaTypes;
            if (ImagePicker.MediaTypeOptions) {
                mediaTypes = ImagePicker.MediaTypeOptions.Images;
            } else if (ImagePicker.MediaType) {
                mediaTypes = ImagePicker.MediaType.Images;
            } else {
                mediaTypes = 'Images';
            }

            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: mediaTypes,
                allowsEditing: true,
                aspect: [4, 3],
                quality: 0.5, // Reduce quality to safer upload
            });

            if (!result.canceled && result.assets && result.assets.length > 0) {
                setProofImage(result.assets[0].uri);
            }
        } catch (error) {
            console.error('Image picker error:', error);
            alert('Error', 'Gagal memilih gambar');
        }
    };

    const handleSubmit = async () => {
        const amountNum = parseInt(amount.replace(/\D/g, ''));

        if (!amountNum || amountNum < parseInt(bankSettings.min_topup || 10000)) {
            alert('Error', `Minimal topup MG ${formatNumber(bankSettings.min_topup || 10000)}`);
            return;
        }

        if (!proofImage) {
            alert('Error', 'Upload bukti transfer terlebih dahulu');
            return;
        }

        setSubmitting(true);

        try {
            // Create form data for file upload
            const formData = new FormData();
            formData.append('member_id', user.id);
            formData.append('jumlah', amountNum);

            if (Platform.OS === 'web') {
                // WEB: Convert URI to Blob
                const response = await fetch(proofImage);
                const blob = await response.blob();
                let filename = proofImage.split('/').pop();

                // Ensure filename has extension (required by PHP validation)
                if (!filename.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)) {
                    filename = `upload_${Date.now()}.jpg`;
                }

                formData.append('bukti', blob, filename);
            } else {
                // NATIVE: Use standard React Native FormData object
                const uri = proofImage;
                const filename = uri.split('/').pop();
                const match = /\.(\w+)$/.exec(filename);
                const type = match ? `image/${match[1]}` : 'image/jpeg';

                formData.append('bukti', {
                    uri: Platform.OS === 'android' ? uri : uri.replace('file://', ''),
                    name: filename,
                    type: type,
                });
            }

            console.log('Sending topup request...', API.TOPUP.REQUEST);

            // Configuration for Axios
            let config = {};

            if (Platform.OS === 'web') {
                // WEB: Let browser set Content-Type with boundary automatically
                config = {
                    headers: {
                        // Do NOT set Content-Type here for Web FormData
                        'Accept': 'application/json',
                    }
                };
            } else {
                // NATIVE: Manual handling often required
                config = {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    transformRequest: (data, headers) => {
                        return formData; // Prevents axios from destroying FormData on Native
                    },
                };
            }

            const response = await axios.post(API.TOPUP.REQUEST, formData, config);

            if (response.data.success) {
                alert(
                    '✅ Berhasil!',
                    'Request topup terkirim. Admin akan memverifikasi bukti transfer Anda.',
                    [{
                        text: 'OK', onPress: () => {
                            setAmount('');
                            setProofImage(null);
                            fetchHistory();
                        }
                    }]
                );
            } else {
                alert('Gagal', response.data.message || 'Terjadi kesalahan server');
            }
        } catch (error) {
            console.error('Submit error:', error);
            let errMsg = 'Gagal mengirim request topup';
            if (error.response) {
                console.error('Response data:', error.response.data);
                errMsg += ': ' + (error.response.data.message || error.response.status);
            } else if (error.request) {
                errMsg += ': Tidak ada respon dari server (Network Error)';
            }
            alert('Error', errMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'approved':
                return { bg: COLORS.success + '15', color: COLORS.success, text: 'Berhasil' };
            case 'rejected':
                return { bg: COLORS.danger + '15', color: COLORS.danger, text: 'Ditolak' };
            default:
                return { bg: COLORS.warning + '15', color: COLORS.warning, text: 'Menunggu' };
        }
    };

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={COLORS.primary} />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <StatusBar barStyle="dark-content" backgroundColor={COLORS.bgDarker} />

            <View style={styles.header}>
                <Text style={styles.headerTitle}>Topup Minguro</Text>
            </View>

            <ScrollView
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.primary} />
                }
            >
                {/* Current Balance */}
                <View style={styles.balanceCard}>
                    <Text style={styles.balanceLabel}>Minguro Saat Ini</Text>
                    <Text style={styles.balanceValue}>{formatCurrency(user?.saldo || 0)}</Text>
                </View>

                {/* Topup Form */}
                <View style={styles.formCard}>
                    <Text style={styles.formTitle}>Request Topup</Text>

                    <Text style={styles.inputLabel}>Jumlah Topup</Text>
                    <View style={styles.amountInput}>
                        <Text style={styles.amountPrefix}>MG</Text>
                        <TextInput
                            style={styles.amountField}
                            placeholder="0"
                            placeholderTextColor={COLORS.textMuted}
                            keyboardType="numeric"
                            value={amount}
                            onChangeText={setAmount}
                        />
                    </View>

                    <View style={styles.quickAmounts}>
                        {quickAmounts.map((val) => (
                            <TouchableOpacity
                                key={val}
                                style={styles.quickBtn}
                                onPress={() => setAmount(val.toString())}
                            >
                                <Text style={styles.quickBtnText}>{formatNumber(val)}</Text>
                            </TouchableOpacity>
                        ))}
                    </View>

                    {/* Proof Upload */}
                    <Text style={[styles.inputLabel, { marginTop: 20 }]}>Bukti Transfer *</Text>
                    <TouchableOpacity style={styles.uploadBox} onPress={pickImage}>
                        {proofImage ? (
                            <Image source={{ uri: proofImage }} style={styles.proofImage} />
                        ) : (
                            <View style={styles.uploadPlaceholder}>
                                <Text style={styles.uploadText}>Tap untuk upload bukti transfer</Text>
                            </View>
                        )}
                    </TouchableOpacity>
                    {proofImage && (
                        <TouchableOpacity onPress={() => setProofImage(null)}>
                            <Text style={styles.removeImage}>Hapus Gambar</Text>
                        </TouchableOpacity>
                    )}

                    {/* Bank Info */}
                    <View style={styles.bankInfo}>
                        <Text style={styles.bankTitle}>Transfer ke:</Text>
                        <View style={styles.bankItem}>
                            <Text style={styles.bankLabel}>Bank {bankSettings.bank_name}</Text>
                            <Text style={styles.bankValue}>{bankSettings.bank_account}</Text>
                            <Text style={styles.bankName}>a.n. {bankSettings.bank_holder}</Text>
                        </View>
                    </View>

                    <TouchableOpacity
                        style={[styles.submitBtn, submitting && styles.submitBtnDisabled]}
                        onPress={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <ActivityIndicator color="#fff" size="small" />
                        ) : (
                            <Text style={styles.submitBtnText}>Kirim Request Topup</Text>
                        )}
                    </TouchableOpacity>
                </View>

                {/* History */}
                <View style={styles.historySection}>
                    <Text style={styles.historyTitle}>Riwayat Topup</Text>

                    {history.length === 0 ? (
                        <View style={styles.emptyState}>
                            <Text style={styles.emptyText}>Belum ada riwayat topup</Text>
                        </View>
                    ) : (
                        history.map((item, index) => {
                            const status = getStatusStyle(item.status);
                            return (
                                <View key={index} style={styles.historyCard}>
                                    <View style={styles.historyContent}>
                                        <Text style={styles.historyAmount}>{formatCurrency(item.jumlah)}</Text>
                                        <Text style={styles.historyDate}>{new Date(item.created_at).toLocaleDateString('id-ID')}</Text>
                                    </View>
                                    <View style={[styles.statusBadge, { backgroundColor: status.bg }]}>
                                        <Text style={[styles.statusText, { color: status.color }]}>{status.text}</Text>
                                    </View>
                                </View>
                            );
                        })
                    )}
                </View>

                <View style={{ height: 100 }} />
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.bgDark,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: COLORS.bgDark,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: Platform.OS === 'web' ? 20 : 50,
        paddingBottom: 16,
        paddingHorizontal: 20,
        backgroundColor: COLORS.bgDarker,
    },
    backBtn: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
    },
    backText: {
        fontSize: 20,
        color: COLORS.textPrimary,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: COLORS.textPrimary,
    },
    placeholder: {
        width: 40,
    },
    scrollView: {
        flex: 1,
        paddingHorizontal: 10,
        paddingTop: 12,
    },
    balanceCard: {
        backgroundColor: COLORS.primary,
        borderRadius: 16,
        padding: 24,
        alignItems: 'center',
        marginBottom: 20,
    },
    balanceLabel: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 12,
        marginBottom: 8,
    },
    balanceValue: {
        color: '#fff',
        fontSize: 28,
        fontWeight: '800',
    },
    formCard: {
        backgroundColor: COLORS.bgCard,
        borderRadius: 16,
        padding: 20,
        marginBottom: 20,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
    },
    formTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: COLORS.textPrimary,
        marginBottom: 20,
    },
    inputLabel: {
        fontSize: 13,
        fontWeight: '600',
        color: COLORS.textSecondary,
        marginBottom: 8,
    },
    amountInput: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.bgDark,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: COLORS.border,
        overflow: 'hidden',
    },
    amountPrefix: {
        paddingHorizontal: 16,
        fontSize: 16,
        fontWeight: '600',
        color: COLORS.textSecondary,
        backgroundColor: COLORS.bgCardHover,
        paddingVertical: 14,
    },
    amountField: {
        flex: 1,
        padding: 14,
        fontSize: 18,
        fontWeight: '700',
        color: COLORS.textPrimary,
    },
    quickAmounts: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 12,
    },
    quickBtn: {
        paddingHorizontal: 14,
        paddingVertical: 8,
        backgroundColor: COLORS.bgDark,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: COLORS.border,
    },
    quickBtnText: {
        fontSize: 12,
        color: COLORS.textSecondary,
        fontWeight: '500',
    },
    uploadBox: {
        backgroundColor: COLORS.bgDark,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: COLORS.border,
        borderStyle: 'dashed',
        overflow: 'hidden',
        minHeight: 150,
    },
    uploadPlaceholder: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 30,
    },
    uploadIcon: {
        fontSize: 40,
        marginBottom: 12,
        opacity: 0.5,
    },
    uploadText: {
        color: COLORS.textMuted,
        fontSize: 13,
        textAlign: 'center',
    },
    proofImage: {
        width: '100%',
        height: 200,
        resizeMode: 'cover',
    },
    removeImage: {
        color: COLORS.danger,
        textAlign: 'center',
        marginTop: 8,
        fontSize: 12,
    },
    bankInfo: {
        backgroundColor: COLORS.bgDark,
        borderRadius: 12,
        padding: 16,
        marginTop: 20,
    },
    bankTitle: {
        fontSize: 13,
        fontWeight: '600',
        color: COLORS.textSecondary,
        marginBottom: 12,
    },
    bankItem: {
        marginBottom: 0,
    },
    bankLabel: {
        fontSize: 12,
        color: COLORS.textMuted,
    },
    bankValue: {
        fontSize: 18,
        fontWeight: '700',
        color: COLORS.primary,
        marginVertical: 4,
    },
    bankName: {
        fontSize: 12,
        color: COLORS.textMuted,
    },
    submitBtn: {
        backgroundColor: COLORS.primary,
        borderRadius: 12,
        padding: 16,
        alignItems: 'center',
        marginTop: 20,
    },
    submitBtnDisabled: {
        opacity: 0.7,
    },
    submitBtnText: {
        color: '#fff',
        fontWeight: '700',
        fontSize: 15,
    },
    historySection: {
        marginTop: 10,
    },
    historyTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: COLORS.textPrimary,
        marginBottom: 16,
    },
    historyCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.bgCard,
        borderRadius: 12,
        padding: 14,
        marginBottom: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.03,
        shadowRadius: 4,
        elevation: 1,
    },
    historyIcon: {
        width: 42,
        height: 42,
        borderRadius: 10,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    historyEmoji: {
        fontSize: 18,
    },
    historyContent: {
        flex: 1,
    },
    historyAmount: {
        fontSize: 15,
        fontWeight: '600',
        color: COLORS.textPrimary,
    },
    historyDate: {
        fontSize: 11,
        color: COLORS.textMuted,
        marginTop: 2,
    },
    statusBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 8,
    },
    statusText: {
        fontSize: 10,
        fontWeight: '600',
    },
    emptyState: {
        alignItems: 'center',
        padding: 40,
        backgroundColor: COLORS.bgCard,
        borderRadius: 12,
    },
    emptyIcon: {
        fontSize: 32,
        marginBottom: 8,
        opacity: 0.5,
    },
    emptyText: {
        color: COLORS.textMuted,
        fontSize: 13,
    },
});
